import { NUMBER_OF_HOMES, NumberOfHomes } from '@schema/initiative'
import { PhaseType } from '@schema/phase'
import {
  InitiativeCreationStep,
  initiativeCreationSteps,
} from '@app/pages/dashboard/initiative/shared/workflow'
import { CreationGuide } from '../blocks/CreationGuide'
import { useEffect, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import Column from '@app/components/layout/Column'
import { useTranslations } from '@i18n/useLanguage'
import { LocalitySelector } from '@app/components/blocks/smartInputs/LocalitySelector'
import { SearchSelectOption } from '@app/components/core/input/searchSelect/SearchSelect'
import { SelectField } from '@app/components/core/form/fields/SelectField'
import { YesNoToggle } from '@app/components/core/toggle/YesNoToggle'
import { Text } from '@app/components/core/typography/Text'
import { Style } from 'inlines'
import { Form } from '@app/components/core/form/Form'
import { FormField } from '@app/components/core/form/FormField'
import { TextAreaField } from '@app/components/core/form/fields/TextAreaField'
import { TextField } from '@app/components/core/form/fields/TextField'
import { RegionSelectorField } from '@app/components/core/form/fields/RegionSelectorField'
import { useInitiativeFormWatchAutosave } from '@app/pages/dashboard/initiative/shared/useInitiativeFormWatchAutosave'
import type { InitiativeWithId } from '@app/pages/dashboard/initiative/shared/initiativeTypes'
import {
  createMembersParametersFormSchema,
  type MembersParametersFormValues,
} from '@app/pages/dashboard/initiative/shared/validation/initiativeValidation'

const labelStyle: Style = {
  color: 'var(--brand-secondary)',
  fontWeight: 500,
}

const CURRENT_STEP = initiativeCreationSteps.membersParameters

export const MembersParametersStep = ({
  initiative,
  selectedPhaseType,
  completeStep,
  uncompleteStep,
}: {
  initiative: InitiativeWithId
  selectedPhaseType: PhaseType
  completeStep: (step: InitiativeCreationStep) => void
  uncompleteStep: (step: InitiativeCreationStep) => void
}) => {
  const translations = useTranslations().app
  const creationTranslations = translations.dashboard.initiative.creation
  const formValidation = useTranslations().app.form.validation

  const schema = useMemo(
    () => createMembersParametersFormSchema(formValidation.required),
    [formValidation.required],
  )

  const defaultValues = useMemo(
    (): MembersParametersFormValues => ({
      searchingForMembers: initiative.searchingForMembers ?? false,
      searchingForMembersDescription:
        initiative.searchingForMembersDescription ?? '',
      numberOfHomes: initiative.numberOfHomes ?? null,
      projectSize: initiative.projectSize ?? '',
      region: {
        key: initiative.location?.address?.region?.id ?? 0,
        value: initiative.location?.address?.region?.name ?? '',
      },
      locality: {
        key: initiative.location?.address?.locality?.id ?? 0,
        value: initiative.location?.address?.locality?.name ?? '',
      },
    }),
    [
      initiative.searchingForMembers,
      initiative.searchingForMembersDescription,
      initiative.numberOfHomes,
      initiative.projectSize,
      initiative.location?.address?.region?.id,
      initiative.location?.address?.region?.name,
      initiative.location?.address?.locality?.id,
      initiative.location?.address?.locality?.name,
    ],
  )

  const emptyLabel = translations.common.notSpecified

  return (
    <CreationGuide
      selectedPhaseType={selectedPhaseType}
      currentStep={CURRENT_STEP}
    >
      <Form<MembersParametersFormValues>
        key={initiative.id}
        schema={schema}
        defaultValues={defaultValues}
        mode="onChange"
        onSubmit={() => undefined}
      >
        <Column gap="2.5rem" alignItems="stretch">
          <MembersParametersOnboardingEffects
            initiativeId={initiative.id}
            completeStep={completeStep}
            uncompleteStep={uncompleteStep}
          />
          <Column gap="0.5rem">
            <Text.Medium isBold={true}>
              {creationTranslations.areYouLookingForMembers}
            </Text.Medium>
            <FormField<MembersParametersFormValues>
              name="searchingForMembers"
              render={({ value, onChange }) => (
                <YesNoToggle
                  yesSelected={Boolean(value)}
                  setYesSelected={onChange}
                />
              )}
            />
          </Column>
          <MembersParametersLookingForMembersDescription />
          <SelectField<MembersParametersFormValues, NumberOfHomes>
            name="numberOfHomes"
            label={creationTranslations.numberOfHomes}
            labelStyle={labelStyle}
            notSelectedLabel={emptyLabel}
            options={Object.values(NUMBER_OF_HOMES).map((item) => ({
              value: item,
              label:
                creationTranslations.numberOfHomesOptions[item] ?? emptyLabel,
            }))}
          />
          <TextField<MembersParametersFormValues>
            name="projectSize"
            label={creationTranslations.projectSize}
            labelStyle={labelStyle}
          />
          <RegionSelectorField<MembersParametersFormValues>
            name="region"
            labelStyle={labelStyle}
          />
          <MembersParametersLocalityField labelStyle={labelStyle} />
        </Column>
      </Form>
    </CreationGuide>
  )
}

const MembersParametersLookingForMembersDescription = () => {
  const searching = useWatch<MembersParametersFormValues>({
    name: 'searchingForMembers',
  })
  const creationTranslations =
    useTranslations().app.dashboard.initiative.creation

  if (!searching) {
    return null
  }

  return (
    <Column gap="0.38rem" alignItems="stretch">
      <Column gap="0.25rem" alignItems="stretch">
        <Text.Medium style={labelStyle}>
          {creationTranslations.lookingForMembersTitle}
        </Text.Medium>
        <Text.Medium color="var(--palette-blue-500)" isBold={true}>
          {creationTranslations.lookingForMembersSubtitle}
        </Text.Medium>
      </Column>
      <TextAreaField<MembersParametersFormValues>
        name="searchingForMembersDescription"
        placeholder={creationTranslations.lookingForMembersPlaceholder}
      />
    </Column>
  )
}

const MembersParametersLocalityField = ({
  labelStyle: ls,
}: {
  labelStyle: Style
}) => {
  const region = useWatch<MembersParametersFormValues, 'region'>({
    name: 'region',
  })
  const filterByRegionId = region.key > 0 ? region.key : undefined

  return (
    <FormField<MembersParametersFormValues>
      name="locality"
      render={({ value, onChange }) => (
        <LocalitySelector
          locality={(value as SearchSelectOption) ?? { key: 0, value: '' }}
          setLocality={onChange}
          filterByRegionId={filterByRegionId}
          labelStyle={ls}
        />
      )}
    />
  )
}

const MembersParametersOnboardingEffects = ({
  initiativeId,
  completeStep,
  uncompleteStep,
}: {
  initiativeId: number
  completeStep: (step: InitiativeCreationStep) => void
  uncompleteStep: (step: InitiativeCreationStep) => void
}): null => {
  const { control, formState } = useFormContext<MembersParametersFormValues>()
  const { isValid } = formState

  useInitiativeFormWatchAutosave({
    initiativeId,
    control,
    toPayload: (v) => ({
      searchingForMembers: v.searchingForMembers,
      searchingForMembersDescription: v.searchingForMembers
        ? v.searchingForMembersDescription
        : '',
      numberOfHomes: v.numberOfHomes ?? undefined,
      projectSize: v.projectSize,
      location: {
        address: {
          locality: v.locality.key > 0 ? { id: v.locality.key } : undefined,
          region: v.region.key > 0 ? { id: v.region.key } : undefined,
          country: undefined,
          municipality: undefined,
          street: null,
          postalCode: null,
        },
        geo: undefined,
      },
      onboardingStep: CURRENT_STEP,
    }),
    watchDeps: (v) =>
      [
        v.searchingForMembers,
        v.searchingForMembersDescription,
        v.numberOfHomes,
        v.projectSize,
        v.region.key,
        v.region.value,
        v.locality.key,
        v.locality.value,
      ] as const,
  })

  useEffect(() => {
    if (isValid) {
      completeStep(CURRENT_STEP)
    } else {
      uncompleteStep(CURRENT_STEP)
    }
  }, [isValid, completeStep, uncompleteStep])

  return null
}
